import {
    ConsentForm,
    ConsentFormCache,
    IAppointmentChecks,
    IClient,
    IClientBooking,
    PortalSupportedFormType,
} from "intake_shared/src/types";

const THERAPY_SERVICES = [
    "Extern Therapy",
    "Child Therapy",
    "Couples Therapy",
    "Family Therapy",
    "In-Person Therapy",
    "Individual Therapy",
    "Teletherapy",
    "Intuitive Eating",
    "Ketamine Assisted Psychotherapy",
    "Ketamine Assisted Psychotherapy (Evaluation)",
    "Ketamine Assisted Psychotherapy (Therapy Intake)",
];

const PSYCHIATRY_SERVICES = [
    "Psychiatry",
    "Psychiatric Counseling",
    "Medication Management",
    "In-Person Med Management",
    "In-Person Psych Counseling",
];

export function getAppointmentConsentForm(
    appointmentPractice: string,
    appointment: IClientBooking,
    consentForms: ConsentFormCache
): ConsentForm | undefined {
    if (appointmentPractice === "HT") {
        return consentForms.HUMANTOLD_CONSENT_FORM;
    }

    if (appointmentPractice === "PH") {
        if (appointment.requested_service === "Nutrition Counseling") {
            return consentForms.PH_NUTRITION_COUNSELING_CONSENT_FORM;
        }
        return consentForms.PH_IN_NETWORK_CONSENT_FORM;
    }

    if (appointmentPractice !== "CHP") {
        return undefined;
    }

    if (
        appointment.requested_service ===
            "Mental Health Assessment: Partial In-Person" ||
        appointment.requested_service ===
            "Mental Health Assessment: Fully In-Person" ||
        appointment.requested_service === "Mental Health Assessment: Virtual" ||
        appointment.requested_service === "Mental Health Assessment: Intern" ||
        appointment.requested_service === "Mental Health Assessment: Extern"
    ) {
        return consentForms.CHP_TESTING_CONSENT_FORM;
    }

    if (
        appointment.requested_service === "Ketamine Assisted Psychotherapy" ||
        appointment.requested_service ===
            "Ketamine Assisted Psychotherapy (Evaluation)" ||
        appointment.requested_service ===
            "Ketamine Assisted Psychotherapy (Therapy Intake)"
    ) {
        return consentForms.KAP_CONSENT;
    }

    if (appointment.requested_service === "Career Assessment") {
        return consentForms.CHP_CAREER_ASSESSMENT_CONSENT_FORM;
    }

    if (appointment.requested_service === "Nutrition Counseling") {
        return consentForms.CHP_NUTRITION_COUNSELING_CONSENT_FORM;
    }

    if (PSYCHIATRY_SERVICES.includes(appointment.requested_service)) {
        return consentForms.CHP_PSYCH_APPT_CONSENT_FORM;
    }

    if (THERAPY_SERVICES.includes(appointment.requested_service)) {
        return consentForms.CHP_EXTERN_THERAPY_CONSENT_FORM;
    }

    return undefined;
}

export function getAppointmentConsentFormType(
    appointmentPractice: string,
    appointment: IClientBooking
): PortalSupportedFormType | undefined {
    if (appointmentPractice === "HT") {
        return "humantold_consent_form";
    }

    if (appointmentPractice === "PH") {
        if (appointment.requested_service === "Nutrition Counseling") {
            return "ph_nutrition_counseling_consent_form";
        }
        return "ph_in_network_consent_form";
    }

    if (appointmentPractice !== "CHP") {
        return undefined;
    }

    if (
        appointment.requested_service ===
            "Mental Health Assessment: Partial In-Person" ||
        appointment.requested_service ===
            "Mental Health Assessment: Fully In-Person" ||
        appointment.requested_service === "Mental Health Assessment: Virtual" ||
        appointment.requested_service === "Mental Health Assessment: Intern" ||
        appointment.requested_service === "Mental Health Assessment: Extern"
    ) {
        return "chp_testing_consent_form";
    }

    if (
        appointment.requested_service === "Ketamine Assisted Psychotherapy" ||
        appointment.requested_service ===
            "Ketamine Assisted Psychotherapy (Evaluation)" ||
        appointment.requested_service ===
            "Ketamine Assisted Psychotherapy (Therapy Intake)"
    ) {
        return "kap_consent";
    }

    if (appointment.requested_service === "Career Assessment") {
        return "chp_career_assessment_consent_form";
    }

    if (appointment.requested_service === "Nutrition Counseling") {
        return "chp_nutrition_counseling_consent_form";
    }

    if (PSYCHIATRY_SERVICES.includes(appointment.requested_service)) {
        return "chp_psych_appt_consent_form";
    }

    if (THERAPY_SERVICES.includes(appointment.requested_service)) {
        return "chp_extern_therapy_consent_form";
    }

    return undefined;
}

export function getHeldAppointmentCheck(
    appointmentCheck: IAppointmentChecks[string] | undefined,
    dateAdded = new Date()
): IAppointmentChecks[string] {
    return (
        appointmentCheck ?? {
            date_added: dateAdded,
            client_requested_call: false,
            consult_call_notes: "",
        }
    );
}

export function getUpdatedHeldConsentCheck(
    appointmentCheck: IAppointmentChecks[string] | undefined,
    consentForm: ConsentForm | undefined,
    isGoodFormOnFile: boolean,
    consentFormRecheckedAt = new Date(),
    dateAdded = new Date()
): IAppointmentChecks[string] {
    const updatedAppointmentCheck: IAppointmentChecks[string] = {
        ...getHeldAppointmentCheck(appointmentCheck, dateAdded),
        consent_form_rechecked: isGoodFormOnFile ? consentFormRecheckedAt : null,
    };

    if (
        isGoodFormOnFile &&
        consentForm?.id &&
        !updatedAppointmentCheck.consent_form
    ) {
        updatedAppointmentCheck.consent_form = consentForm;
    }

    return updatedAppointmentCheck;
}

export function getTemplateConsentForm(
    templateName: string,
    consentForms: ConsentFormCache
): ConsentForm | undefined {
    switch (templateName) {
        case "humantold_consent_form":
            return consentForms.HUMANTOLD_CONSENT_FORM;
        case "parkhill_consent_form":
            return consentForms.PH_IN_NETWORK_CONSENT_FORM;
        case "parkhill_nutrition_consent_form":
            return consentForms.PH_NUTRITION_COUNSELING_CONSENT_FORM;
        case "chp_testing_consent_form":
            return consentForms.CHP_TESTING_CONSENT_FORM;
        case "kap_consent":
            return consentForms.KAP_CONSENT;
        case "chp_psychiatry_consent_form":
            return consentForms.CHP_PSYCH_APPT_CONSENT_FORM;
        case "chp_nutrition_counseling_consent_form":
            return consentForms.CHP_NUTRITION_COUNSELING_CONSENT_FORM;
        case "chp_therapy_consent_form":
            return consentForms.CHP_EXTERN_THERAPY_CONSENT_FORM;
        case "chp_career_assessment_consent_form":
            return consentForms.CHP_CAREER_ASSESSMENT_CONSENT_FORM;
        default:
            return undefined;
    }
}

export function getConsentFormLinks(consentForm?: ConsentForm) {
    let consentFormLinkAdult = "";
    let consentFormLinkMinor = "";
    let consentFormLinkMinorSecondaryCustodian = "";
    let consentFormLinkCollateralAdult = "";
    let consentFormLinkCollateralMinor = "";
    let singleConsentFormLink = "";

    const types = consentForm?.types ?? [];

    const SECONDARY_CUSTODIAN_NAMES = [
        "Minors - Secondary Custodian",
        "Minor - Secondary Custodian",
    ];
    const SUPPLEMENTAL_TYPE_NAMES = [
        ...SECONDARY_CUSTODIAN_NAMES,
        "Collateral Consent",
        "Collateral Consent MINOR",
    ];

    // Supplemental types are resolved separately and never affect the primary
    // link resolution strategy.
    const primaryTypes = types.filter(
        (t) => !SUPPLEMENTAL_TYPE_NAMES.includes(t.name)
    );

    types.forEach((type) => {
        if (SECONDARY_CUSTODIAN_NAMES.includes(type.name)) {
            consentFormLinkMinorSecondaryCustodian = type.link;
        }
        if (type.name === "Collateral Consent") {
            consentFormLinkCollateralAdult = type.link;
        }
        if (type.name === "Collateral Consent MINOR") {
            consentFormLinkCollateralMinor = type.link;
        }
    });

    if (primaryTypes.length === 1) {
        // One primary type is age-agnostic regardless of its name, preserving the
        // original single-type behavior even if it is named "Adults" or "Minors".
        singleConsentFormLink = primaryTypes[0].link;
    } else {
        primaryTypes.forEach((type) => {
            if (type.name === "Adults") {
                consentFormLinkAdult = type.link;
            } else if (type.name === "Minors") {
                consentFormLinkMinor = type.link;
            }
        });

        // No Adults/Minors names found in a multi-primary-type form — fall back to
        // first primary type as a single link.
        if (!consentFormLinkAdult && !consentFormLinkMinor && primaryTypes.length) {
            singleConsentFormLink = primaryTypes[0].link;
        }
    }

    // When there is only a single catch-all link, populate the age-specific
    // fields from it so templates that render consent_form_link_adult /
    // consent_form_link_minor directly always have a non-empty value.
    if (singleConsentFormLink) {
        if (!consentFormLinkAdult) consentFormLinkAdult = singleConsentFormLink;
        if (!consentFormLinkMinor) consentFormLinkMinor = singleConsentFormLink;
    }

    return {
        consent_form_link_adult: consentFormLinkAdult,
        consent_form_link_minor: consentFormLinkMinor,
        consent_form_link_minor_secondary_custodian:
            consentFormLinkMinorSecondaryCustodian,
        consent_form_link_collateral_adult: consentFormLinkCollateralAdult,
        consent_form_link_collateral_minor: consentFormLinkCollateralMinor,
        single_consent_form_link: singleConsentFormLink,
    };
}

export function getRequiredConsentFormLink(
    consentForm: ConsentForm | undefined,
    isMinor: boolean
): string {
    const links = getConsentFormLinks(consentForm);

    return (
        links.single_consent_form_link ||
        (isMinor ? links.consent_form_link_minor : links.consent_form_link_adult)
    );
}

export function getClientWithoutConsentReminderMetadata(client: IClient): IClient {
    const sanitizedClient = { ...client };
    if (!client.appointment_checks) {
        delete sanitizedClient.appointment_checks;
        return sanitizedClient;
    }

    sanitizedClient.appointment_checks = Object.entries(
        client.appointment_checks
    ).reduce((sanitizedAppointmentChecks, [appointmentId, appointmentCheck]) => {
        const sanitizedAppointmentCheck = { ...appointmentCheck };
        delete sanitizedAppointmentCheck.consent_form_reminder_sent;
        delete sanitizedAppointmentCheck.consent_form_reminder_count;
        sanitizedAppointmentChecks[appointmentId] = sanitizedAppointmentCheck;
        return sanitizedAppointmentChecks;
    }, {} as IAppointmentChecks);
    return sanitizedClient;
}

export function mergeAppointmentChecksPreservingConsentCcNotes(
    refreshedAppointmentChecks: IAppointmentChecks,
    localAppointmentChecks: IAppointmentChecks | undefined
): IAppointmentChecks {
    // Async consent and attendee operations return persisted appointment checks.
    // Preserve note values that were edited locally while those operations ran.
    return Object.entries(localAppointmentChecks || {}).reduce(
        (mergedAppointmentChecks, [appointmentId, localAppointmentCheck]) => {
            if (
                !Object.prototype.hasOwnProperty.call(
                    localAppointmentCheck,
                    "consent_cc_notes"
                ) ||
                localAppointmentCheck.consent_cc_notes ===
                    mergedAppointmentChecks[appointmentId]?.consent_cc_notes
            ) {
                return mergedAppointmentChecks;
            }

            mergedAppointmentChecks[appointmentId] = {
                ...(mergedAppointmentChecks[appointmentId] || localAppointmentCheck),
                consent_cc_notes: localAppointmentCheck.consent_cc_notes,
            };
            return mergedAppointmentChecks;
        },
        { ...refreshedAppointmentChecks }
    );
}
